import React, { useState, useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LuLayoutDashboard,
  LuBriefcase,
  LuMic,
  LuGraduationCap,
  LuFileText,
  LuUser,
  LuCreditCard,
  LuLogOut,
  LuMenu,
  LuSparkles,
  LuSun,
  LuMoon,
  LuSend,
  LuTarget,
  LuRoute,
} from "react-icons/lu";
import { Logout } from "../../store/actions/AuthActions";
import { setShowPro } from "../../store/actions/actions";
import { Brand, Btn, useClickOutside, initialsOf } from "./Ui";
import FeedbackWidget from "./FeedbackWidget";
import InfoHelper from "./InfoHelper";
import useAppBootstrap from "../useAppBootstrap";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LuLayoutDashboard },
  { to: "/career-journey", label: "Career Journey", icon: LuRoute },
  { to: "/search-job", label: "Jobs", icon: LuBriefcase },
  { to: "/job-match", label: "Match a job", icon: LuTarget },
  { to: "/recording", label: "Mock interview", icon: LuMic },
  { to: "/interview-prep", label: "Interview prep", icon: LuGraduationCap },
  { to: "/aspire-quest", label: "Aspire Quest", icon: LuSparkles },
  { to: "/resume-upload", label: "Resume", icon: LuFileText },
  { to: "/outreach", label: "Outreach", icon: LuSend },
];

/* The account links render in their own group below the main nav, so they were
   missing from the title lookup and those two pages showed a blank topbar. */
const ACCOUNT_NAV = [
  { to: "/profile", label: "Profile" },
  { to: "/subscription", label: "Subscription" },
];

/* Routes not present in the sidebar nav still need a browser-tab title. */
const EXTRA_TITLES = [
  { to: "/", label: "Dashboard" },
  { to: "/job/detailed", label: "Job Details" },
  { to: "/interview-exam", label: "Interview" },
  { to: "/videoInterview", label: "Video Interview" },
  { to: "/checkout", label: "Checkout" },
];

/* One-line subtitle shown under the page title in the top bar — a quick "what
   this page is for" on every tab, the way ElevateStaffing labels each screen.
   Keep these short, plain, and action-oriented. */
const SUBTITLES = {
  "/dashboard": "Your job search at a glance — matches, activity and next steps",
  "/": "Your job search at a glance — matches, activity and next steps",
  "/career-journey": "Talk to your AI coach, get a plan, and track your progress",
  "/search-job": "Roles matched to your resume — search, filter and apply",
  "/job-match": "Paste a job to see how you match and how to close the gaps",
  "/recording": "Practice a live interview with an AI co-pilot in real time",
  "/interview-prep": "Rehearse interview questions and get scored AI feedback",
  "/aspire-quest": "Test your skills with timed assessments and coding exercises",
  "/resume-upload": "Upload your resume and keep your profile details up to date",
  "/outreach": "Personalised email campaigns from your own mailbox",
  "/profile": "Manage your account details and preferences",
  "/subscription": "Your plan, billing and renewal",
  "/job/detailed": "Full role details, requirements and how to apply",
};

/* Routes that need the full viewport without shell chrome (exam / recording surfaces) */
export const BARE_ROUTES = ["/interview-exam", "/videoInterview"];

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth.auth);
  const profileData = useSelector((s) => s.profile.profileData);
  const subscriptionNextAction = useSelector((s) => s.profile.subscriptionNextAction);
  const [sideOpen, setSideOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  /* Light is the default look for the authenticated app; only an explicit
     "dark" choice (persisted) overrides it. */
  const [theme, setTheme] = useState(() => localStorage.getItem("cs_theme") === "dark" ? "dark" : "light");
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const menuRef = useClickOutside(closeMenu);
  useAppBootstrap();

  /* Theme is applied only while the authenticated shell is mounted; public
     pages keep the default dark look. Choice persists in localStorage. */
  useEffect(() => {
    if (theme === "light") {
      document.documentElement.setAttribute("data-cs-theme", "light");
    } else {
      document.documentElement.removeAttribute("data-cs-theme");
    }
    localStorage.setItem("cs_theme", theme);
    return () => document.documentElement.removeAttribute("data-cs-theme");
  }, [theme]);

  /* Keep the browser-tab title in sync with the current route. These authed
     pages render no <Helmet>, so without this every screen kept the generic
     index.html title; public pages still set their own title via <SEO>. */
  useEffect(() => {
    const label = [...NAV, ...ACCOUNT_NAV, ...EXTRA_TITLES].find((n) => n.to === pathname)?.label;
    document.title = label
      ? `${label} | CareerSavvy`
      : "CareerSavvy | Your AI-Powered Job Search Assistant";
  }, [pathname]);

  if (BARE_ROUTES.includes(pathname)) {
    return <>{children}</>;
  }

  const email = auth?.email || "";
  const displayName =
    profileData?.first_name || profileData?.full_name || profileData?.name || (email ? email.split("@")[0] : "You");
  /* Plan detection: the backend's subscription_next_action ("Pro User") is the
     authoritative signal (set by the shell bootstrap from send_resume_details).
     Fall back to the auth-store plan, then to the raw localStorage tokenDetails
     (the way the legacy Header read it). */
  let planName = auth?.subscription;
  if (!planName) {
    try {
      const stored = JSON.parse(localStorage.getItem("userDetails") || "null");
      planName = stored?.subscription;
    } catch (err) {
      planName = "";
    }
  }
  const isPro =
    subscriptionNextAction === "Pro User" ||
    (planName && String(planName).toLowerCase() !== "free");

  const doLogout = () => {
    setMenuOpen(false);
    dispatch(Logout(navigate));
  };

  const title = [...NAV, ...ACCOUNT_NAV].find((n) => n.to === pathname)?.label || "";
  const subtitle = SUBTITLES[pathname] || "";
  /* Contextual "?" help keys off the route (home "/" shares the dashboard help). */
  const helpSlug = pathname === "/" ? "/dashboard" : pathname;

  return (
    <div className="csx">
      <div className="csx-app">
        {sideOpen ? <div className="appscrim" onClick={() => setSideOpen(false)} /> : null}
        <aside className={`appside${sideOpen ? " open" : ""}`}>
          <div className="appside-head">
            <Brand to="/dashboard" />
          </div>
          <nav className="appside-nav">
            {NAV.map((n) => {
              const Icon = n.icon;
              const on = pathname === n.to || (n.to === "/dashboard" && pathname === "/");
              return (
                <Link key={n.to} to={n.to} className={`appside-link${on ? " on" : ""}`} onClick={() => setSideOpen(false)}>
                  <Icon size={18} />
                  <span>{n.label}</span>
                </Link>
              );
            })}
            <div className="appside-section">
              <span className="t-label">Account</span>
            </div>
            <Link
              to="/profile"
              className={`appside-link${pathname === "/profile" ? " on" : ""}`}
              onClick={() => setSideOpen(false)}
            >
              <LuUser size={18} />
              <span>Profile</span>
            </Link>
            <Link
              to="/subscription"
              className={`appside-link${pathname === "/subscription" ? " on" : ""}`}
              onClick={() => setSideOpen(false)}
            >
              <LuCreditCard size={18} />
              <span>Subscription</span>
            </Link>
          </nav>
          <div className="appside-foot">
            {isPro ? (
              <div className="appside-pro">
                <span className="badge badge-accent">Pro</span>
                <p className="t-xs mt-1" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {email}
                </p>
              </div>
            ) : (
              <div className="appside-upgrade">
                <div className="t-sm t-ink" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>
                  Unlock everything
                </div>
                <p className="t-xs mt-1">Match analysis, tailored resumes, and voice interviews.</p>
                <Btn
                  size="sm"
                  full
                  className="mt-2 btn-pill btn-mint"
                  onClick={() => {
                    dispatch(setShowPro(true));
                    navigate("/subscription");
                  }}
                >
                  <LuSparkles size={14} /> Upgrade
                </Btn>
              </div>
            )}
          </div>
        </aside>

        <div className="appmain">
          <div className="apptop">
            <button type="button" className="btn btn-quiet btn-sm apptop-burger" onClick={() => setSideOpen(true)} aria-label="Open menu">
              <LuMenu size={19} />
            </button>
            <div className="apptop-head grow">
              <div className="apptop-title-row">
                <div className="apptop-title">{title}</div>
                <InfoHelper slug={helpSlug} />
              </div>
              {subtitle ? <div className="apptop-sub">{subtitle}</div> : null}
            </div>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              onClick={() => setTheme((t) => (t === "light" ? "dark" : "light"))}
            >
              {theme === "light" ? <LuMoon size={17} /> : <LuSun size={17} />}
            </button>
            <div style={{ position: "relative" }} ref={menuRef}>
              <button type="button" className="avatar" aria-label="Account menu" onClick={() => setMenuOpen((v) => !v)}>
                {initialsOf(displayName || email)}
              </button>
              {menuOpen ? (
                <div className="menu-pop">
                  <div style={{ padding: "8px 11px" }}>
                    <div className="t-sm t-ink" style={{ fontWeight: 600 }}>
                      {displayName}
                    </div>
                    <div className="t-xs" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {email}
                    </div>
                  </div>
                  <hr className="divider" style={{ margin: "4px 0" }} />
                  <Link to="/profile" onClick={closeMenu}>
                    <LuUser size={16} /> Profile
                  </Link>
                  <Link to="/subscription" onClick={closeMenu}>
                    <LuCreditCard size={16} /> Subscription
                  </Link>
                  <button type="button" className="menu-danger" onClick={doLogout}>
                    <LuLogOut size={16} /> Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <main className="appbody">{children}</main>
        </div>
        <FeedbackWidget />
      </div>
    </div>
  );
}
